import { useState, useEffect, useContext, useRef } from "react";
import {
  Grid,
  Typography,
  Checkbox,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { EventContext } from "../../../contexts/EventContext";
import EditProfileDialog from "../../../components/EditProfileDialog";
import SwapDialog from "./SwapDialog";
import TransferDialog from "./TransferDialog";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const AttendeeList = () => {
  const {
    userData,
    pairingEvent,
    attendees,
    setAttendees,
    pagination,
    setPagination,
    percentages,
    setPercentages,
  } = useContext(EventContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [presentLabel, setPresentLabel] = useState("Mark Present");
  const [anchor, setAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  // const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeUserOpen, setRemoveUserOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [ticketingEnabled, setTicketingEnabled] = useState(false);
  const [hasWaiver, setHasWaiver] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);
  const openSearch = () => {
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearching(false);
    setSearchResults([]);
  };
  const runSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }

    const result = await request(
      `pairingevent/search?pairingEventId=${
        pairingEvent._id
      }&q=${encodeURIComponent(searchQuery)}`
    );

    if (result.success) {
      setSearchResults(result.attendees);
      setSearching(true);
    } else {
      toast.error(result.error || "Search failed");
    }
  };
  const handleSearchSelect = (attendee) => {
    setSelectedUser(attendee);
    setEditUserOpen(true);
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearching(false);
  };

  useEffect(() => {
    if (userData?.org?.config?.ticketingEnabled) {
      setTicketingEnabled(true);
    }
  }, [userData]);

  useEffect(() => {
    if (pairingEvent && pairingEvent?.event?.waiver) {
      setHasWaiver(true);
    }
  }, [pairingEvent]);

  const handleCheck = (i) => {
    if (!attendees[i].checkedIn) {
      attendees[i].checkedIn = true;
      updpateCheckinStatus(attendees[i]._id, true);
    } else {
      attendees[i].checkedIn = false;
      updpateCheckinStatus(attendees[i]._id, false);
    }
    setAttendees([...attendees]);
  };

  const updpateCheckinStatus = async (userId, checkedIn) => {
    const body = {
      eventId: pairingEvent.event._id,
      userId,
      checkedIn,
    };
    const result = await request("events/updatecheckin", "post", body);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleMenuOpen = (event, item) => {
    setAnchor(event.currentTarget);
    setMenuOpen(true);
    setSelectedUser(item);
    if (item?.isPresent) {
      setPresentLabel("Mark Not Present");
    } else {
      setPresentLabel("Mark Present");
    }
  };

  const getAttendees = async (offset = 0, append = false) => {
    if (loadingMore) return;

    setLoadingMore(true);

    const result = await request(
      `pairingevent/attendees?pairingEventId=${pairingEvent._id}&limit=100&offset=${offset}`
    );

    if (result.success) {
      if (append) {
        setAttendees((prev) => [...prev, ...result.attendees]);
      } else {
        setAttendees(result.attendees);
      }

      setPercentages(result.percentages);
      setPagination(result.pagination);

      if (result.attendees.length < 100) {
        setHasMore(false);
      }
    }

    setLoadingMore(false);
  };

  useEffect(() => {
    if (pairingEvent?._id) {
      setHasMore(true);
      setSearching(false);
      getAttendees(0, false);
    }
  }, [pairingEvent]);

  useEffect(() => {
    if (searching) return;
    if (!loadMoreRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          const nextOffset =
            (pagination?.offset || 0) + (pagination?.limit || 100);
          getAttendees(nextOffset, true);
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [loadMoreRef.current, hasMore, loadingMore, pagination, searching]);

  const handleDialogClose = () => {
    getAttendees();
    setEditUserOpen(false);
    setRemoveUserOpen(false);
    setSelectedUser(null);
    setMenuOpen(false);
  };

  const handleEdit = () => {
    setEditUserOpen(true);
    setMenuOpen(false);
  };

  const handleSwap = () => {
    setSwapOpen(true);
  };

  const handleSwapClose = () => {
    getAttendees();
    setSwapOpen(false);
    setSelectedUser(null);
    setMenuOpen(false);
  };

  const handleTransfer = () => {
    setTransferOpen(true);
    setMenuOpen(false);
  };

  const handleRemove = () => {
    setRemoveUserOpen(true);
    setMenuOpen(false);
  };

  const handleConfirm = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent.event._id,
    };
    const result = await request("events/removeattendee", "post", body);
    if (result.success) {
      toast.success("User removed from event");
      handleDialogClose();
    }
  };

  const markPresent = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent?.event?._id,
    };
    let delta = 0;
    if (selectedUser?.isPresent === true) {
      delta = -1;
    } else if (selectedUser?.isPresent === false) {
      delta = 1;
    }

    const result = await request(`events/markpresent`, "post", body);
    if (result.success) {
      setAttendees((prev) =>
        prev.map((attendee) =>
          attendee._id === selectedUser._id
            ? { ...attendee, isPresent: result.isPresent }
            : attendee
        )
      );
      setPercentages((prev) => ({
        ...prev,
        isPresent: (prev.isPresent || 0) + delta,
      }));
      if (result?.isPresent === true) {
        toast.success("Attendee marked present");
      } else if (result?.isPresent === false) {
        toast.success("Attendee marked not present");
      }
    }
    setMenuOpen(false);
  };

  const updateNoShow = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent?.event?._id,
    };
    const result = await request("events/updatenoshow", "post", body);
    if (result.success) {
      setAttendees((prev) =>
        prev.map((attendee) =>
          attendee._id === selectedUser._id
            ? { ...attendee, noShow: result.noShow }
            : attendee
        )
      );
      if (result?.noShow === true) {
        toast.success("Attendee as not attending");
      } else if (result?.isPresent === false) {
        toast.success("Attendee marked as attending");
      }
    }
    setMenuOpen(false);
  };

  const renderAttendees = () => {
    if (attendees.length > 0) {
      return (
        <Grid container direction="column">
          <Grid item style={{ marginBottom: -20 }}>
            <Grid container direction="row" alignItems="center">
              <Grid item xs={6}>
                <Typography variant="button">name</Typography>
              </Grid>
              <Grid
                item
                xs={4}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <Typography variant="button" align="center">
                  checked in
                </Typography>
              </Grid>
              <Grid
                item
                xs={1}
                style={{ display: "flex", justifyContent: "center" }}
              ></Grid>
            </Grid>
          </Grid>
          {attendees.map((item, i) => {
            return (
              <Grid item key={item._id}>
                <Grid container direction="row" alignItems="center">
                  <Grid
                    item
                    xs={6}
                    style={{ display: "flex", flexDirection: "row" }}
                  >
                    {item?.incompleteProfile && (
                      <Tooltip title="Attendee's profile is incomplete. In order to check into the event, they must complete their profile">
                        <PriorityHighIcon />
                      </Tooltip>
                    )}
                    {item?.noShow && (
                      <Tooltip title="Attendee indicated that they are unable to attend">
                        <EventBusyIcon />
                      </Tooltip>
                    )}
                    {hasWaiver && item?.signedWaiver === false ? (
                      <Tooltip title="Attendee has not yet signed the waiver required for this event.">
                        <EditIcon style={{ width: 20, marginRight: 10 }} />
                      </Tooltip>
                    ) : null}
                    {pairingEvent?.event?.trackIsPresent && item?.isPresent && (
                      <Tooltip title="Attendee's ticket has been scanned">
                        <CheckIcon sx={{ color: "green" }} />
                      </Tooltip>
                    )}
                    <Typography>
                      {item.profile?.firstName} {item.profile?.lastName}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <Checkbox
                      checked={item.checkedIn}
                      onChange={() => handleCheck(i)}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <MoreVertIcon
                      onClick={(event) => handleMenuOpen(event, item)}
                      style={{ cursor: "pointer" }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
          <div ref={loadMoreRef} style={{ height: 20 }} />
          {loadingMore && (
            <Grid container justifyContent="center" style={{ padding: 16 }}>
              <CircularProgress size={24} />
            </Grid>
          )}
          <Menu
            anchorEl={anchor}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          >
            {pairingEvent?.event?.trackIsPresent && (
              <>
                <MenuItem onClick={markPresent}>{presentLabel}</MenuItem>
                <Divider />
              </>
            )}
            <MenuItem onClick={handleEdit}>Edit</MenuItem>
            <MenuItem onClick={handleSwap}>Swap</MenuItem>
            {ticketingEnabled && (
              <MenuItem onClick={handleTransfer}>Transfer</MenuItem>
            )}
            <MenuItem onClick={handleRemove}>Remove Attendee</MenuItem>
            <Divider />
            <MenuItem onClick={updateNoShow}>
              {selectedUser?.noShow ? "Cancel No-Show" : "Mark No-Show"}
            </MenuItem>
          </Menu>
          {/* <EditUserDialog
            open={editUserOpen}
            close={handleDialogClose}
            user={selectedUser}
          /> */}
          <EditProfileDialog
            open={editUserOpen}
            close={handleDialogClose}
            user={selectedUser}
            eventId={pairingEvent?.event?._id}
          />
          {/* <AssignUserEvent
            open={removeUserOpen}
            close={handleDialogClose}
            user={selectedUser}
            action="remove"
          /> */}
        </Grid>
      );
    } else {
      return (
        <Typography>
          No attendees in database. Try syncing with Eventbrite
        </Typography>
      );
    }
  };

  return (
    <>
      <Grid container direction="column" spacing={3}>
        <Grid
          item
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {pairingEvent?.event?.trackIsPresent && (
            <Typography variant="subtitle2">
              Present: {percentages?.isPresent ? percentages.isPresent : ""}
            </Typography>
          )}
          <Typography variant="subtitle2">
            Checked In:{" "}
            {percentages?.totalCheckedIn ? percentages.totalCheckedIn : ""}
          </Typography>
          <Typography variant="subtitle2">
            Boys: {percentages?.boysCheckedIn ? percentages.boysCheckedIn : ""}
          </Typography>
          <Typography variant="subtitle2">
            Girls:{" "}
            {percentages?.girlsCheckedIn ? percentages.girlsCheckedIn : ""}
          </Typography>
          <IconButton onClick={openSearch} size="small">
            <SearchIcon />
          </IconButton>
        </Grid>
        <Grid item>{renderAttendees()}</Grid>
        <Dialog open={searchOpen} onClose={closeSearch} fullWidth maxWidth="sm">
          <DialogTitle>Search Attendees</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  runSearch();
                }
              }}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={runSearch} size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption">
              Start typing to find a specific attendee
            </Typography>
            {searchResults.length > 0 && (
              <Grid container direction="column" spacing={1} style={{ marginTop: 16 }}>
                {searchResults.map((item) => (
                  <Grid
                    item
                    key={item._id}
                    onClick={() => handleSearchSelect(item)}
                    style={{ cursor: "pointer", padding: 8, borderRadius: 4 }}
                  >
                    <Typography variant="body1">
                      {item.profile?.firstName} {item.profile?.lastName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item.email}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSearch}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={removeUserOpen} onClose={() => setRemoveUserOpen(false)}>
          <DialogTitle>Please confirm</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove{" "}
              <strong>
                {selectedUser?.profile?.firstName}{" "}
                {selectedUser?.profile?.lastName}
              </strong>{" "}
              from the event?
            </Typography>
            {ticketingEnabled ? (
              <Typography style={{ paddingTop: 20 }}>
                Removing an attendee here will <strong>not</strong> issue a
                refund. If the ticket was purchased through this system, please
                refund via Users → Search Attendee. From there, you can also
                remove them from the event. Note: if you remove them here,
                ticket availability will update on this system and any linked
                platforms. (similar to refunds)
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveUserOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogActions>
        </Dialog>
        <SwapDialog
          open={swapOpen}
          close={handleSwapClose}
          user={selectedUser}
        />
        <TransferDialog
          open={transferOpen}
          close={() => {
            setTransferOpen(false);
            getAttendees();
          }}
          user={selectedUser}
        />
      </Grid>
    </>
  );
};

export default AttendeeList;
